import { computed, inject, Injectable, signal } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import { UtilService } from "@core/utils/util.service";
import { environment } from "@env/environment";
import { injectQuery, injectQueryClient, queryOptions } from "@ngneat/query";
import { derivedAsync } from "ngxtension/derived-async";
import { HorusSnackbarStatusComponent } from "@shared/components/domain/horus-snackbar-status/horus-snackbar-status.component";
import { ClientData, SearchClientForm } from "@shared/models/ClientData";
import { Tenant } from "@core/models/Tenant";
import dayjs from "dayjs";
import { firstValueFrom } from "rxjs";
import { SearchService } from "./search.service";

@Injectable({
  providedIn: "root",
})
export class SearchState {
  private readonly searchService = inject(SearchService);
  private readonly utilService = inject(UtilService);
  private readonly query = injectQuery();
  private readonly queryClient = injectQueryClient();
  private readonly snackBar = inject(MatSnackBar);
  private readonly activatedRoute = inject(ActivatedRoute);

  readonly searchValue = signal<string>("");
  readonly dateRange = signal<string>(this.getDefaultDateRange());
  readonly selectedTenant = signal<Tenant | undefined>(undefined);
  readonly currentPage = signal<string | undefined>(undefined);
  readonly isOpen = signal<boolean>(false);

  readonly tenantId = computed(() => this.selectedTenant()?.tenantId);
  readonly searchType = computed(() =>
    this.utilService.getTypeData(this.searchValue()),
  );

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  toggle(): void {
    this.isOpen.update((value) => !value);
  }

  readonly isValidSearch = computed(
    () => !!this.searchValue() && !!this.tenantId() && !!this.dateRange(),
  );

  readonly searchIcon = computed(() => {
    const value = this.searchValue();
    return !value || value === ""
      ? undefined
      : environment.commons.searchTypeAndIcon.get(this.searchType()) ||
          "searchErrorIcon";
  });

  private getClientQueryOptions(
    searchValue: string,
    tenantId: string,
    searchType: string,
  ) {
    return queryOptions({
      queryKey: ["client", searchValue, tenantId, searchType] as const,
      queryFn: async () => {
        return await this.searchService.getClient(
          searchValue,
          tenantId,
          searchType,
        );
      },
      enabled: !!searchValue && !!tenantId && searchType !== 'error',
    });
  }

  readonly clientQuery = derivedAsync(() => {
    const searchValue = this.searchValue();
    const tenantId = this.tenantId();
    const searchType = this.searchType();

    if (!searchValue || !tenantId) {
      return undefined;
    }

    return this.query(
      this.getClientQueryOptions(searchValue, tenantId!, searchType),
    ).result$;
  });

  readonly isLoading = computed(() => {
    const query = this.clientQuery();
    return query?.isLoading ?? false;
  });

  readonly clientData = computed(() => {
    const query = this.clientQuery();
    return query?.data ?? null;
  });

  readonly error = computed(() => {
    const query = this.clientQuery();
    return query?.error ?? null;
  });

  readonly hasClientData = computed(() => !!this.clientData());
  readonly isInitialState = computed(() => !this.searchValue());

  readonly clientDni = computed(() => {
    const client = this.clientData();
    return client?.document ?? null;
  });

  readonly isCreating = signal<boolean>(false);
  readonly createClientError = signal<any>(null);

  setSearchValue(value: string): void {
    this.searchValue.set(value);
  }

  setDateRange(range: string): void {
    this.dateRange.set(range);
  }

  setTenant(tenant: Tenant): void {
    this.selectedTenant.set(tenant);
  }

  setCurrentPage(page: string): void {
    this.currentPage.set(page);
  }

  updateSearchForm(form: SearchClientForm): void {
    this.searchValue.set(form.searchValue || "");
    this.dateRange.set(form.dateRange || this.getDefaultDateRange());
    this.selectedTenant.set(form.tenant);
    this.currentPage.set(form.currentPage);
  }

  reset(): void {
    this.searchValue.set("");
    this.selectedTenant.set(undefined);
    this.dateRange.set(this.getDefaultDateRange());
    this.currentPage.set(undefined);
    this.isOpen.set(false);
  }

  async prefetchClient(
    searchValue: string,
    tenantId: string,
    searchType: string,
  ): Promise<void> {
    return this.queryClient.prefetchQuery(
      this.getClientQueryOptions(searchValue, tenantId, searchType),
    );
  }

  async createClient(clientData: ClientData): Promise<ClientData> {
    const tenantId = this.selectedTenant()?.tenantId;
    if (!tenantId) {
      throw new Error("No tenant selected");
    }

    this.isCreating.set(true);
    this.createClientError.set(null);

    try {
      const createdClient = await this.searchService.createClient(
        clientData,
        tenantId,
      );

      this.snackBar.openFromComponent(HorusSnackbarStatusComponent, {
        duration: environment.timeSnackbar,
        panelClass: "snackbar-container",
        horizontalPosition: "end",
        verticalPosition: "top",
        data: {
          typeSnackbar: "success",
          title: "¡Listo!",
          text: "El cliente ha sido creado exitosamente.",
        },
      });

      await this.queryClient.invalidateQueries({
        queryKey: ["client"],
      });

      this.isCreating.set(false);
      return createdClient;
    } catch (error) {
      this.createClientError.set(error);
      this.isCreating.set(false);

      this.snackBar.openFromComponent(HorusSnackbarStatusComponent, {
        duration: environment.timeSnackbar,
        panelClass: "snackbar-container",
        horizontalPosition: "end",
        verticalPosition: "top",
        data: {
          typeSnackbar: "error",
          title: "Lo sentimos",
          text: "Hubo un error al intentar crear a este cliente, inténtalo nuevamente.",
        },
      });

      throw error;
    }
  }

  async getPhoneParamsUrl(): Promise<string> {
    let phone = "";
    try {
      const queryParams = await firstValueFrom(this.activatedRoute.queryParams);
      phone = queryParams[environment.namePhoneParamURL] || "";
    } catch (e) {
      phone = "";
    }
    return phone;
  }

  private getDefaultDateRange(): string {
    return (
      dayjs().subtract(3, "month").format("DD/MM/YYYY") +
      " - " +
      dayjs().format("DD/MM/YYYY")
    );
  }
}
